import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, ClipboardList, Eye } from 'lucide-react';
import { SalesOrderForm, blankOrder } from '@/components/sales/SalesOrderForm';
import { SalesOrderView } from '@/components/sales/SalesOrderView';
import { ApprovalBadge, StatusTabs, RejectDialog, isApprover, userHasRole, apiError } from '@/components/sales/ApprovalBits';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function SalesOrders() {
  const { API, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tab, setTab] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [saving, setSaving] = useState(false);
  const canCreate = userHasRole(user, ['admin', 'manager', 'sales', 'enterprise_sales']);
  const approver = isApprover(user);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sales-orders`);
      setOrders(data);
    } catch (e) { toast.error(apiError(e, 'Failed to load sales orders')); }
  }, [API]);

  useEffect(() => {
    load();
    axios.get(`${API}/customers`).then(r => setCustomers(r.data.filter(c => c.customer_type !== 'lco'))).catch(() => {});
  }, [API, load]);

  const counts = useMemo(() => orders.reduce((a, o) => ({ ...a, [o.status]: (a[o.status] || 0) + 1 }), { all: orders.length }), [orders]);
  const shown = tab === 'all' ? orders : orders.filter(o => o.status === tab);

  const save = async (payload, files) => {
    if (!payload.customer_id) return toast.error('Please select a customer');
    setSaving(true);
    try {
      const { data } = editing
        ? await axios.put(`${API}/sales-orders/${editing.order_id}`, payload)
        : await axios.post(`${API}/sales-orders`, { ...payload, submit_for_approval: false });
      for (const file of files) {
        const fd = new FormData(); fd.append('file', file);
        await axios.post(`${API}/sales-orders/${data.order_id}/attachments`, fd);
      }
      if (!editing && payload.submit_for_approval) await axios.post(`${API}/sales-orders/${data.order_id}/submit`);
      toast.success(payload.submit_for_approval ? 'Sales order submitted for approval' : 'Draft saved');
      setFormOpen(false); setEditing(null); load();
    } catch (e) { toast.error(apiError(e, 'Failed to save sales order')); }
    finally { setSaving(false); }
  };

  const act = async (order, action, body) => {
    try {
      await axios.post(`${API}/sales-orders/${order.order_id}/${action}`, body);
      toast.success(`Order ${order.order_number} ${action === 'submit' ? 'submitted' : action + 'd'}`);
      setViewing(null); setRejecting(null); load();
    } catch (e) { toast.error(apiError(e, 'Action failed')); }
  };

  const openEdit = (o) => { setViewing(null); setEditing(o); setFormOpen(true); };

  return (
    <div className="space-y-6" data-testid="sales-orders-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-semibold text-slate-900 tracking-tight">Sales Orders</h2>
          <p className="text-slate-600 mt-1">Internet Leased Line &amp; MPLS orders — {approver ? 'review and approve' : 'raise and track your orders'}</p>
        </div>
        {canCreate && (
          <Button data-testid="new-sales-order-btn" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> New Sales Order
          </Button>
        )}
      </div>

      <StatusTabs value={tab} onChange={setTab} counts={counts} prefix="so" />

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Order #</TableHead><TableHead>Customer</TableHead><TableHead>Service</TableHead>
                <TableHead>Bandwidth</TableHead><TableHead>OTC / MRC</TableHead><TableHead>Status</TableHead>
                <TableHead>Raised By</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-500" data-testid="so-empty">
                  <ClipboardList className="h-10 w-10 mx-auto text-slate-300 mb-2" />No sales orders here yet.
                </TableCell></TableRow>
              ) : shown.map(o => (
                <TableRow key={o.order_id} data-testid={`so-row-${o.order_number}`} className="hover:bg-slate-50/60">
                  <TableCell className="font-mono text-sm">{o.order_number}</TableCell>
                  <TableCell className="font-medium">{o.customer_name}</TableCell>
                  <TableCell>{o.service_type}</TableCell>
                  <TableCell>{o.bandwidth_mbps} Mbps</TableCell>
                  <TableCell className="text-sm">{inr(o.otc)} / {inr(o.mrc)}</TableCell>
                  <TableCell><ApprovalBadge status={o.status} testId={`so-status-${o.order_number}`} /></TableCell>
                  <TableCell className="text-xs text-slate-500">{o.created_by}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" data-testid={`so-view-${o.order_number}`} onClick={() => setViewing(o)}><Eye className="h-3.5 w-3.5 mr-1" />View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="sales-order-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">{editing ? `Edit ${editing.order_number}` : 'New Sales Order'}</DialogTitle>
            <DialogDescription>Internet Leased Line or MPLS — orders go live only after Admin/Manager approval.</DialogDescription>
          </DialogHeader>
          {formOpen && <SalesOrderForm initial={editing ? { ...blankOrder(), ...editing } : blankOrder()} customers={customers} onSubmit={save} saving={saving} />}
        </DialogContent>
      </Dialog>

      <SalesOrderView order={viewing} onClose={() => setViewing(null)} user={user} API={API}
        onEdit={openEdit} onSubmit={(o) => act(o, 'submit')} onApprove={(o) => act(o, 'approve')} onReject={(o) => setRejecting(o)} onChanged={load} />

      <RejectDialog open={!!rejecting} prefix="so" title={`Reject ${rejecting?.order_number || ''}`}
        onClose={() => setRejecting(null)} onConfirm={(reason) => act(rejecting, 'reject', { reason })} />
    </div>
  );
}
